/**
 * Phase 8.1 — Help Center hub at /help.
 *
 * Public, SEO-friendly page: hero + search → "Ask Qelli" CTA → category
 * cards (icons + counts) → featured / most-recent docs.
 *
 * The Qelli CTA banner is auth-aware: signed-in users see "Open Qelli";
 * signed-out users see a value-prop line "Sign in for 24/7 help from Qelli,
 * QorTrace's Advanced AI Assistant" → routes to /signin.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Search, Sparkles, Rocket, Radar, ShieldCheck, Wallet, CreditCard,
  Users, Plug, FileCheck2, ArrowRight, BookOpen,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useUserAuth } from "../lib/userAuth";
import { QelliPublicCTA } from "../components/qelli/QelliPublicCTA";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  "getting-started": Rocket,
  "pqc-scanner":     Radar,
  "audits":          ShieldCheck,
  "atlas":           Wallet,
  "billing":         CreditCard,
  "team":            Users,
  "integrations":    Plug,
  "compliance":      FileCheck2,
};


function QelliCTA({ account }) {
  return (
    <div className="qt-card help-qelli-cta" data-testid="help-qelli-cta">
      <div className="help-qelli-cta-glow" />
      <div className="help-qelli-cta-body">
        <div className="qt-label" style={{ color: "var(--qt-cyan)" }}>QELLI · QORTRACE'S ADVANCED AI ASSISTANT</div>
        <h3 className="help-qelli-cta-title">
          {account
            ? "Need help right now?"
            : "Get 24/7 help from Qelli — instantly."}
        </h3>
        <p className="help-qelli-cta-sub">
          {account
            ? "Open Qelli from the bottom-right of any QorTrace page. She's trained on every doc here and walks you through anything step by step."
            : "Sign in (or create a free account) to unlock 24/7, step-by-step guidance from Qelli — QorTrace's AI assistant trained exclusively on our docs and methodology."}
        </p>
        <div className="help-qelli-cta-actions">
          {account ? (
            <button
              type="button"
              className="qt-btn-primary"
              data-testid="help-qelli-cta-open"
              onClick={() => {
                if (typeof window !== "undefined") {
                  window.sessionStorage.setItem("qt_qelli_open", "1");
                  // Force the QelliWidget to react to storage flip — re-dispatch
                  // a custom event the widget listens for.
                  window.dispatchEvent(new CustomEvent("qt-qelli-open"));
                }
              }}
            >
              <Sparkles size={14} /> Open Qelli
            </button>
          ) : (
            <Link to="/signin" className="qt-btn-primary" data-testid="help-qelli-cta-signin">
              <Sparkles size={14} /> Sign in to chat with Qelli
            </Link>
          )}
          <Link to="/docs" className="qt-btn-ghost" data-testid="help-qelli-cta-docs">
            <BookOpen size={14} /> Browse all docs
          </Link>
        </div>
      </div>
    </div>
  );
}


function CategoryCard({ category }) {
  const Icon = CATEGORY_ICONS[category.id] || BookOpen;
  return (
    <Link
      to={`/help/${category.id}`}
      className="qt-card help-cat-card"
      data-testid={`help-cat-${category.id}`}
    >
      <div className="help-cat-icon"><Icon size={18} /></div>
      <div className="help-cat-body">
        <div className="help-cat-title">{category.label}</div>
        <p className="help-cat-blurb">{category.blurb}</p>
        <div className="help-cat-count">
          {category.page_count} guide{category.page_count === 1 ? "" : "s"}
          <ArrowRight size={12} />
        </div>
      </div>
    </Link>
  );
}


function FeaturedRow({ pages }) {
  if (!pages?.length) return null;
  return (
    <div className="help-featured" data-testid="help-featured">
      <div className="qt-label" style={{ color: "var(--qt-text-3)" }}>RECENTLY UPDATED</div>
      <div className="help-featured-grid">
        {pages.slice(0, 6).map((p) => (
          <Link
            key={p.slug}
            to={`/docs/${p.slug}`}
            className="qt-card help-featured-card"
            data-testid="help-featured-card"
          >
            <div className="help-featured-title">{p.title}</div>
            <p className="help-featured-summary">{p.summary || ""}</p>
            <div className="help-featured-meta">
              <ArrowRight size={12} />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}


export default function HelpCenter() {
  const { account } = useUserAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cats, feat] = await Promise.all([
          axios.get(`${API}/api/help/categories`),
          axios.get(`${API}/api/help/featured?limit=6`),
        ]);
        if (cancelled) return;
        setCategories(cats.data?.categories || []);
        setFeatured(feat.data?.pages || []);
      } catch {
        if (!cancelled) { setCategories([]); setFeatured([]); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Live search → reuse the existing /api/wiki/search endpoint.
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/api/wiki/search`, {
          params: { q: q.trim() },
        });
        if (!cancelled) setResults(r.data?.results || []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    const top = results[0];
    if (top) navigate(`/docs/${top.slug}`);
  };

  return (
    <div className="min-h-screen qt-radial-bg" data-testid="help-center">
      <Header />
      <QelliPublicCTA variant="banner" />
      <section className="max-w-[1200px] mx-auto px-6 sm:px-8 py-12">
        {/* Hero */}
        <div className="help-hero">
          <div className="qt-label">QORTRACE HELP CENTER</div>
          <h1 className="qt-h2 text-4xl sm:text-5xl mt-2">
            How can we help?
          </h1>
          <p className="mt-3 max-w-2xl" style={{ color: "var(--qt-text-2)" }}>
            Step-by-step guides, reference docs, and 24/7 AI help from{" "}
            <span style={{ color: "var(--qt-cyan)" }}>Qelli</span>{" "}
            — QorTrace's advanced AI assistant.
          </p>
          <div className="mt-3">
            <Link
              to="/trends"
              className="qt-mono text-xs"
              data-testid="help-trends-link"
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "6px 12px",
                border: "1px solid rgba(0, 229, 255, 0.35)",
                background: "rgba(0, 229, 255, 0.06)",
                color: "var(--qt-cyan)",
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                borderRadius: 999,
                textDecoration: "none",
              }}
            >
              See what other teams are asking → /TRENDS
            </Link>
          </div>

          <form onSubmit={onSearchSubmit} className="help-search" role="search">
            <Search size={16} style={{ color: "var(--qt-text-3)" }} />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search docs — e.g. 'how do I scan a wallet'"
              className="help-search-input"
              data-testid="help-search-input"
            />
            {searching && <span className="qt-mono text-xs" style={{ color: "var(--qt-text-3)" }}>…</span>}
          </form>

          {results.length > 0 && (
            <div className="help-search-results" data-testid="help-search-results">
              {results.slice(0, 6).map((r) => (
                <Link
                  key={r.slug}
                  to={`/docs/${r.slug}`}
                  className="help-search-row"
                  data-testid="help-search-row"
                >
                  <BookOpen size={12} />
                  <div className="help-search-row-body">
                    <div className="help-search-row-title">{r.title}</div>
                    <div className="help-search-row-summary">{r.body_excerpt || r.summary}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Qelli CTA — auth-aware */}
        <div className="mt-10">
          <QelliCTA account={account} />
        </div>

        {/* Category grid */}
        <div className="mt-12">
          <div className="qt-label" style={{ color: "var(--qt-text-3)" }}>BROWSE BY TOPIC</div>
          <div className="help-cat-grid mt-4" data-testid="help-categories">
            {categories.map((c) => (
              <CategoryCard key={c.id} category={c} />
            ))}
          </div>
        </div>

        {/* Featured / recently updated */}
        <div className="mt-14">
          <FeaturedRow pages={featured} />
        </div>
      </section>
      <Footer />
    </div>
  );
}
